<template>
    <div class="platform-console">
        <header class="platform-console__header">
            <div class="platform-console__brand">
                <span class="platform-console__logo">A</span>
                <div>
                    <strong>Argus</strong>
                    <small>Платформа</small>
                </div>
            </div>
            <div class="platform-console__header-actions">
                <button class="platform-button platform-button--ghost" @click="reload" :disabled="loading">
                    <i class="fas fa-sync-alt mr-2" :class="{'fa-spin': loading}"></i>Обновить
                </button>
                <div class="platform-console__user">
                    <strong>{{ user.displayName || user.login }}</strong>
                    <small>{{ user.login }}</small>
                </div>
                <button class="platform-button platform-button--quiet" @click="logout">Выйти</button>
            </div>
        </header>

        <main class="platform-console__content">
            <div class="platform-console__title-row">
                <div>
                    <div class="platform-kicker">Центр управления</div>
                    <h1>Состояние платформы</h1>
                    <p>Значимые сбои всех компаний и системные события, требующие реакции.</p>
                </div>
                <div class="platform-console__updated">
                    Актуально на<br><strong>{{ formatDate(summary.generatedAt) }}</strong>
                </div>
            </div>

            <div v-if="pageError" class="alert alert-danger">{{ pageError }}</div>

            <section class="platform-summary" aria-label="Сводка инцидентов">
                <article class="platform-summary__card platform-summary__card--critical">
                    <span>Критические</span><strong>{{ summary.critical }}</strong><small>нужна реакция</small>
                </article>
                <article class="platform-summary__card platform-summary__card--warning">
                    <span>Предупреждения</span><strong>{{ summary.warning }}</strong><small>активные</small>
                </article>
                <article class="platform-summary__card">
                    <span>Всего активных</span><strong>{{ summary.active }}</strong><small>включая подтверждённые</small>
                </article>
                <article class="platform-summary__card">
                    <span>Подтверждены</span><strong>{{ summary.acknowledged }}</strong><small>в работе у администратора</small>
                </article>
                <article class="platform-summary__card" :class="{'platform-summary__card--critical': summary.deadDeliveries}">
                    <span>Не доставлены</span><strong>{{ summary.deadDeliveries }}</strong><small>{{ summary.pendingDeliveries }} ждут отправки</small>
                </article>
            </section>

            <nav class="platform-tabs">
                <button :class="{active: activeTab === 'security'}" @click="activeTab = 'security'">Безопасность</button>
                <button :class="{active: activeTab === 'incidents'}" @click="activeTab = 'incidents'">
                    Инциденты <span>{{ total }}</span>
                </button>
                <button :class="{active: activeTab === 'companies'}" @click="activeTab = 'companies'">
                    Компании <span>{{ companies.length }}</span>
                </button>
                <button :class="{active: activeTab === 'telegram'}" @click="activeTab = 'telegram'">Telegram</button>
            </nav>

            <section v-if="activeTab === 'incidents'" class="platform-panel">
                <form class="platform-filters" @submit.prevent="applyFilters">
                    <select v-model="filters.status" @change="applyFilters">
                        <option value="active">Активные</option>
                        <option value="open">Новые</option>
                        <option value="acknowledged">Подтверждённые</option>
                        <option value="resolved">Восстановленные</option>
                        <option value="all">Все</option>
                    </select>
                    <select v-model="filters.severity" @change="applyFilters">
                        <option value="">Любая критичность</option>
                        <option value="critical">Критические</option>
                        <option value="warning">Предупреждения</option>
                        <option value="info">Информационные</option>
                    </select>
                    <select v-model="filters.company" @change="applyFilters">
                        <option value="">Все компании</option>
                        <option v-for="company in companies" :key="company.key" :value="company.key">
                            {{ company.name }}
                        </option>
                    </select>
                    <div class="platform-filters__search">
                        <input v-model.trim="filters.query" type="search" placeholder="Код, ресурс или текст">
                        <button type="submit" aria-label="Найти"><i class="fas fa-search"></i></button>
                    </div>
                </form>

                <div class="platform-table-wrap">
                    <table class="platform-table">
                        <thead>
                            <tr>
                                <th>Инцидент</th><th>Компания / ресурс</th><th>Проявления</th><th>Последнее</th><th>Статус</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="incident in incidents" :key="incident.id" @click="openIncident(incident)">
                                <td>
                                    <span class="platform-badge" :class="severityClass(incident.severity)">{{ severityLabel(incident.severity) }}</span>
                                    <strong class="platform-table__title">{{ incident.title }}</strong>
                                    <small>{{ incident.code }}</small>
                                </td>
                                <td><strong>{{ companyLabel(incident.companyKey) }}</strong><small>{{ incident.resourceKey || '—' }}</small></td>
                                <td class="platform-table__count">{{ incident.occurrenceCount }}</td>
                                <td>{{ formatDate(incident.lastSeenAt) }}</td>
                                <td><span class="platform-status" :class="`platform-status--${incident.status}`">{{ statusLabel(incident.status) }}</span></td>
                                <td class="platform-table__action">
                                    <button
                                        v-if="incident.status === 'open'"
                                        class="platform-button platform-button--ack"
                                        :disabled="acknowledgingId === incident.id"
                                        @click.stop="acknowledge(incident)"
                                    >Принять</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="!loading && !incidents.length" class="platform-empty">
                        <i class="far fa-check-circle"></i>
                        <strong>По выбранным условиям инцидентов нет</strong>
                    </div>
                    <div v-if="loading" class="platform-loading"><span class="spinner-border"></span></div>
                </div>

                <footer v-if="total > limit" class="platform-pagination">
                    <span>Показано {{ offset + 1 }}–{{ Math.min(offset + incidents.length, total) }} из {{ total }}</span>
                    <div>
                        <button :disabled="offset === 0 || loading" @click="previousPage">Назад</button>
                        <button :disabled="offset + limit >= total || loading" @click="nextPage">Дальше</button>
                    </div>
                </footer>
            </section>

            <section v-else-if="activeTab === 'companies'" class="platform-panel platform-companies">
                <div class="platform-panel__intro">
                    <div>
                        <h2>Контуры компаний</h2>
                        <p>Сейчас это безопасный обзор подключённых баз. Редактирование реестра компаний будет отдельным следующим этапом.</p>
                    </div>
                </div>
                <div class="platform-company-grid">
                    <article v-for="company in companies" :key="company.key" class="platform-company-card">
                        <div class="platform-company-card__head">
                            <span class="platform-company-card__type">{{ company.type === 'platform' ? 'Платформа' : 'Компания' }}</span>
                            <span class="platform-health" :class="`platform-health--${company.databaseStatus}`">
                                {{ company.databaseStatus === 'unavailable' ? 'База недоступна' : 'База доступна' }}
                            </span>
                        </div>
                        <h3>{{ company.name }}</h3>
                        <code>{{ company.key }}</code>
                        <div class="platform-company-card__stats">
                            <div><strong>{{ company.activeIncidentCount }}</strong><span>активных</span></div>
                            <div><strong>{{ company.criticalIncidentCount }}</strong><span>критических</span></div>
                        </div>
                        <small v-if="company.lastIncidentAt">Последний сбой: {{ formatDate(company.lastIncidentAt) }}</small>
                    </article>
                </div>
            </section>
            <PlatformSecurity v-else-if="activeTab === 'security'"/>
            <section v-else>
                <PlatformTelegramConnection ref="telegramConnection" @changed="$refs.telegramOperations.reload()"/>
                <PlatformTelegramOperations ref="telegramOperations" @connect-candidate="$refs.telegramConnection.openCandidate($event)" @account-changed="$refs.telegramConnection.loadStatus()"/>
            </section>
        </main>

        <b-modal v-model="showIncidentModal" size="lg" hide-footer title="Карточка инцидента">
            <div v-if="selectedIncident" class="platform-incident-detail">
                <div class="platform-incident-detail__heading">
                    <span class="platform-badge" :class="severityClass(selectedIncident.severity)">{{ severityLabel(selectedIncident.severity) }}</span>
                    <span class="platform-status" :class="`platform-status--${selectedIncident.status}`">{{ statusLabel(selectedIncident.status) }}</span>
                </div>
                <h3>{{ selectedIncident.title }}</h3>
                <p>{{ selectedIncident.message || 'Дополнительного сообщения нет.' }}</p>
                <dl>
                    <dt>Код</dt><dd>{{ selectedIncident.code }}</dd>
                    <dt>Компания</dt><dd>{{ companyLabel(selectedIncident.companyKey) }}</dd>
                    <dt>Ресурс</dt><dd>{{ selectedIncident.resourceKey || '—' }}</dd>
                    <dt>Первое проявление</dt><dd>{{ formatDate(selectedIncident.firstSeenAt) }}</dd>
                    <dt>Последнее проявление</dt><dd>{{ formatDate(selectedIncident.lastSeenAt) }}</dd>
                    <dt>Количество</dt><dd>{{ selectedIncident.occurrenceCount }}</dd>
                </dl>
                <h4>Технический контекст</h4>
                <pre>{{ contextText(selectedIncident.context) }}</pre>
                <button
                    v-if="selectedIncident.status === 'open'"
                    class="platform-button platform-button--ack"
                    @click="acknowledge(selectedIncident)"
                >Принять инцидент в работу</button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import platformApi from '@/platform/api';
import PlatformSecurity from './PlatformSecurity.vue';
import {logoutSession, lockSession} from '@/utils/sessionSecurity';
import PlatformTelegramConnection from './PlatformTelegramConnection.vue';
import PlatformTelegramOperations from './PlatformTelegramOperations.vue';
import {
    clearPlatformSession,
    getPlatformRefreshToken,
    getPlatformUserFromToken
} from '@/platform/session';

const EMPTY_SUMMARY = {
    active: 0,
    critical: 0,
    warning: 0,
    acknowledged: 0,
    pendingDeliveries: 0,
    deadDeliveries: 0,
    generatedAt: null
};

export default {
    name: 'PlatformConsole',
    components: {PlatformTelegramConnection, PlatformTelegramOperations, PlatformSecurity},
    data() {
        return {
            user: getPlatformUserFromToken() || {},
            summary: {...EMPTY_SUMMARY},
            companies: [],
            incidents: [],
            total: 0,
            limit: 25,
            offset: 0,
            activeTab: 'incidents',
            loading: false,
            pageError: '',
            acknowledgingId: null,
            selectedIncident: null,
            showIncidentModal: false,
            filters: {status: 'active', severity: '', company: '', query: ''}
        };
    },
    created() {
        this.reload();
    },
    methods: {
        async reload() {
            if (this.$refs.telegramConnection) this.$refs.telegramConnection.loadStatus();
            this.loading = true;
            this.pageError = '';
            try {
                const [summary, companies, me] = await Promise.all([
                    platformApi.get('/summary'),
                    platformApi.get('/companies'),
                    platformApi.get('/auth/me')
                ]);
                this.summary = summary.data;
                this.companies = companies.data.items || [];
                this.user = me.data.user || this.user;
                await this.loadIncidents(false);
            } catch (error) {
                this.pageError = this.errorMessage(error);
            } finally {
                this.loading = false;
            }
        },
        async loadIncidents(manageLoading = true) {
            if (manageLoading) this.loading = true;
            this.pageError = '';
            try {
                const params = {
                    status: this.filters.status,
                    limit: this.limit,
                    offset: this.offset
                };
                if (this.filters.severity) params.severity = this.filters.severity;
                if (this.filters.company) params.company = this.filters.company;
                if (this.filters.query) params.query = this.filters.query;
                const response = await platformApi.get('/incidents', {params});
                this.incidents = response.data.items || [];
                this.total = response.data.total || 0;
            } catch (error) {
                this.pageError = this.errorMessage(error);
            } finally {
                if (manageLoading) this.loading = false;
            }
        },
        applyFilters() {
            this.offset = 0;
            this.loadIncidents();
        },
        previousPage() {
            this.offset = Math.max(0, this.offset - this.limit);
            this.loadIncidents();
        },
        nextPage() {
            this.offset += this.limit;
            this.loadIncidents();
        },
        openIncident(incident) {
            this.selectedIncident = incident;
            this.showIncidentModal = true;
        },
        async acknowledge(incident) {
            const confirmed = window.confirm(
                'Принять инцидент в работу? Повторные Telegram-уведомления по нему будут приостановлены до восстановления и нового открытия сбоя.'
            );
            if (!confirmed) return;
            this.acknowledgingId = incident.id;
            try {
                const response = await platformApi.post(`/incidents/${incident.id}/acknowledge`);
                const updated = response.data;
                this.incidents = this.incidents.map(item => item.id === updated.id ? updated : item);
                if (this.selectedIncident && this.selectedIncident.id === updated.id) this.selectedIncident = updated;
                const summary = await platformApi.get('/summary');
                this.summary = summary.data;
            } catch (error) {
                this.pageError = this.errorMessage(error);
            } finally {
                this.acknowledgingId = null;
            }
        },
        async logout() {
            try {
                await logoutSession('platform');
            } catch (error) {
                lockSession('platform', error); return;
            }
            clearPlatformSession();
            await this.$router.replace('/platform/login');
        },
        companyLabel(key) {
            const company = this.companies.find(item => item.key === (key || 'common'));
            return company ? company.name : (key || 'Платформа Argus');
        },
        severityLabel(value) {
            return {critical: 'Критический', warning: 'Предупреждение', info: 'Информация'}[value] || value;
        },
        severityClass(value) {
            return `platform-badge--${value || 'info'}`;
        },
        statusLabel(value) {
            return {open: 'Новый', acknowledged: 'В работе', resolved: 'Восстановлен'}[value] || value;
        },
        formatDate(value) {
            if (!value) return '—';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '—';
            return new Intl.DateTimeFormat('ru-RU', {
                day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'
            }).format(date);
        },
        contextText(context) {
            return JSON.stringify(context || {}, null, 2);
        },
        errorMessage(error) {
            return error.response && error.response.data && error.response.data.msg
                ? error.response.data.msg
                : 'Не удалось получить данные платформы.';
        }
    }
};
</script>

<style scoped>
.platform-console { min-height: 100vh; background: #f1f4f5; color: #182229; }
.platform-console__header {
    position: sticky; top: 0; z-index: 20; min-height: 70px; padding: 0 clamp(18px, 4vw, 58px);
    display: flex; align-items: center; justify-content: space-between; gap: 20px;
    background: #102f37; color: #fff; box-shadow: 0 2px 16px rgba(11,31,36,.17);
}
.platform-console__brand, .platform-console__header-actions, .platform-console__user { display: flex; align-items: center; }
.platform-console__brand { gap: 12px; }
.platform-console__logo { display: grid; place-items: center; width: 36px; height: 36px; border-radius: 10px; background: #e9ac42; color: #102f37; font-weight: 900; }
.platform-console__brand strong, .platform-console__brand small, .platform-console__user strong, .platform-console__user small { display: block; }
.platform-console__brand strong { line-height: 1; font-size: 17px; }
.platform-console__brand small, .platform-console__user small { color: rgba(255,255,255,.58); font-size: 11px; }
.platform-console__header-actions { gap: 14px; }
.platform-console__user { display: block; min-width: 130px; padding-left: 12px; border-left: 1px solid rgba(255,255,255,.14); }
.platform-console__user strong { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; }
.platform-console__content { max-width: 1500px; margin: 0 auto; padding: 42px clamp(16px, 4vw, 58px) 80px; }
.platform-console__title-row { display: flex; justify-content: space-between; gap: 30px; align-items: flex-end; margin-bottom: 30px; }
.platform-kicker { color: #b27112; font-size: 11px; font-weight: 800; letter-spacing: .16em; text-transform: uppercase; }
.platform-console h1 { margin: 5px 0 7px; font-size: clamp(32px, 4vw, 48px); letter-spacing: -.045em; }
.platform-console__title-row p { margin: 0; color: #60747b; }
.platform-console__updated { color: #718187; text-align: right; font-size: 12px; }
.platform-console__updated strong { color: #354b52; }
.platform-button { border: 0; border-radius: 7px; padding: 9px 14px; font-size: 13px; font-weight: 700; cursor: pointer; }
.platform-button:disabled { opacity: .5; cursor: default; }
.platform-button--ghost { border: 1px solid rgba(255,255,255,.2); background: transparent; color: #fff; }
.platform-button--quiet { background: transparent; color: #e3b763; }
.platform-button--ack { background: #dff2e7; color: #17643b; white-space: nowrap; }
.platform-summary { display: grid; grid-template-columns: repeat(5, minmax(150px, 1fr)); gap: 12px; margin-bottom: 30px; }
.platform-summary__card { min-height: 134px; padding: 19px; display: flex; flex-direction: column; border: 1px solid #dce3e5; border-radius: 12px; background: #fff; }
.platform-summary__card span { color: #61757b; font-size: 12px; font-weight: 700; }
.platform-summary__card strong { margin: auto 0 0; font-size: 38px; line-height: 1; letter-spacing: -.05em; }
.platform-summary__card small { margin-top: 7px; color: #8a989d; }
.platform-summary__card--critical { border-top: 3px solid #c84949; }
.platform-summary__card--critical strong { color: #a82f2f; }
.platform-summary__card--warning { border-top: 3px solid #dda537; }
.platform-tabs { display: flex; flex-wrap: wrap; gap: 0 28px; margin-bottom: -1px; padding-left: 4px; }
.platform-tabs button { padding: 12px 2px; border: 0; border-bottom: 3px solid transparent; background: transparent; color: #667a80; font-weight: 700; }
.platform-tabs button.active { border-color: #176573; color: #123f49; }
.platform-tabs span { display: inline-block; min-width: 22px; padding: 2px 6px; margin-left: 5px; border-radius: 999px; background: #dce5e7; font-size: 11px; }
.platform-panel { min-height: 350px; border: 1px solid #dce3e5; border-radius: 11px; background: #fff; overflow: hidden; }
.platform-filters { display: grid; grid-template-columns: 180px 190px minmax(190px, 1fr) minmax(220px, 1.3fr); gap: 10px; padding: 16px; border-bottom: 1px solid #e4e9ea; background: #f9fbfb; }
.platform-filters select, .platform-filters input { width: 100%; height: 40px; border: 1px solid #ccd7da; border-radius: 7px; background: #fff; color: #334a51; padding: 0 11px; font-size: 13px; }
.platform-filters__search { position: relative; }
.platform-filters__search input { padding-right: 42px; }
.platform-filters__search button { position: absolute; right: 0; top: 0; width: 40px; height: 40px; border: 0; background: transparent; color: #176573; }
.platform-table-wrap { position: relative; min-height: 250px; overflow-x: auto; }
.platform-table { width: 100%; min-width: 960px; border-collapse: collapse; font-size: 13px; }
.platform-table th { padding: 11px 14px; border-bottom: 1px solid #e5eaeb; color: #7c8c91; text-align: left; font-size: 10px; letter-spacing: .07em; text-transform: uppercase; }
.platform-table td { padding: 14px; border-bottom: 1px solid #edf0f1; vertical-align: middle; }
.platform-table tbody tr { cursor: pointer; transition: background .12s; }
.platform-table tbody tr:hover { background: #f6f9f9; }
.platform-table td strong, .platform-table td small { display: block; }
.platform-table td small { margin-top: 4px; color: #829095; }
.platform-table__title { max-width: 410px; margin-top: 7px; }
.platform-table__count { font-size: 18px; font-weight: 700; }
.platform-table__action { width: 80px; }
.platform-badge { display: inline-block; padding: 3px 7px; border-radius: 5px; font-size: 10px; font-weight: 800; letter-spacing: .03em; text-transform: uppercase; }
.platform-badge--critical { background: #f7dede; color: #9e2e2e; }
.platform-badge--warning { background: #fff0cd; color: #93620d; }
.platform-badge--info { background: #dceef2; color: #276573; }
.platform-status { display: inline-block; padding: 5px 8px; border-radius: 999px; background: #e7ecee; color: #596c72; font-size: 11px; font-weight: 700; }
.platform-status--open { background: #f9e1e1; color: #a33333; }
.platform-status--acknowledged { background: #fff0cf; color: #8a5c0a; }
.platform-status--resolved { background: #dff2e7; color: #17643b; }
.platform-loading { position: absolute; inset: 0; display: grid; place-items: center; background: rgba(255,255,255,.72); color: #176573; }
.platform-empty { min-height: 250px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #819096; }
.platform-empty i { margin-bottom: 10px; color: #4d9b72; font-size: 34px; }
.platform-pagination { display: flex; justify-content: space-between; align-items: center; padding: 14px 16px; color: #75878c; font-size: 12px; }
.platform-pagination button { margin-left: 6px; padding: 7px 12px; border: 1px solid #ccd7da; border-radius: 6px; background: #fff; }
.platform-panel__intro { padding: 24px; border-bottom: 1px solid #e4e9ea; }
.platform-panel__intro h2 { margin: 0 0 6px; font-size: 22px; }
.platform-panel__intro p { max-width: 740px; margin: 0; color: #687c82; }
.platform-company-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; padding: 18px; }
.platform-company-card { padding: 20px; border: 1px solid #dce3e5; border-radius: 10px; background: #fbfcfc; }
.platform-company-card__head { display: flex; justify-content: space-between; gap: 12px; align-items: center; }
.platform-company-card__type { color: #74868c; font-size: 10px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.platform-health { color: #267347; font-size: 11px; font-weight: 700; }
.platform-health::before { content: ''; display: inline-block; width: 7px; height: 7px; margin-right: 6px; border-radius: 50%; background: #44a66d; }
.platform-health--unavailable { color: #a33333; }
.platform-health--unavailable::before { background: #c84949; }
.platform-company-card h3 { margin: 24px 0 2px; font-size: 19px; }
.platform-company-card code { color: #6f8288; }
.platform-company-card__stats { display: flex; gap: 36px; margin: 25px 0 14px; }
.platform-company-card__stats strong, .platform-company-card__stats span { display: block; }
.platform-company-card__stats strong { font-size: 27px; line-height: 1; }
.platform-company-card__stats span { margin-top: 5px; color: #7e8d92; font-size: 11px; }
.platform-company-card > small { color: #7e8d92; }
.platform-incident-detail__heading { display: flex; gap: 8px; align-items: center; margin-bottom: 14px; }
.platform-incident-detail h3 { margin-bottom: 8px; }
.platform-incident-detail > p { color: #61757b; }
.platform-incident-detail dl { display: grid; grid-template-columns: 160px 1fr; gap: 7px 12px; margin: 24px 0; }
.platform-incident-detail dt { color: #75878c; }
.platform-incident-detail dd { margin: 0; word-break: break-word; }
.platform-incident-detail h4 { font-size: 14px; }
.platform-incident-detail pre { max-height: 280px; padding: 14px; overflow: auto; border-radius: 7px; background: #122c33; color: #dce9eb; font-size: 12px; white-space: pre-wrap; }
@media (max-width: 1050px) {
    .platform-summary { grid-template-columns: repeat(3, 1fr); }
    .platform-filters { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 720px) {
    .platform-console__header { align-items: flex-start; padding-top: 12px; padding-bottom: 12px; }
    .platform-console__header-actions { flex-wrap: wrap; justify-content: flex-end; gap: 7px; }
    .platform-console__user { display: none; }
    .platform-console__title-row { display: block; }
    .platform-console__updated { margin-top: 15px; text-align: left; }
    .platform-summary { grid-template-columns: repeat(2, 1fr); }
    .platform-summary__card { min-height: 118px; }
    .platform-filters { grid-template-columns: 1fr; }
    .platform-pagination { align-items: flex-start; gap: 12px; }
}
@media (max-width: 430px) {
    .platform-summary { grid-template-columns: 1fr; }
    .platform-console__brand div { display: none; }
    .platform-console__content { padding-top: 28px; }
}
</style>
